import {
  computeReminders,
  expenseForEvent,
  reconcileReminders,
  type DesiredReminder,
  type DomainAnimal,
  type DomainEvent,
  type DomainProtocol,
  type DomainReminder,
} from '@dairy/domain';
import {
  EVENT_COST_CATEGORY,
  FARM_TIME_ZONE,
  recordIds,
  todayInTimeZone,
  type AnimalRecord,
  type EventRecord,
  type ExpenseRecord,
  type ReminderRecord,
} from '@dairy/shared';
import { db, getMeta, setMeta } from '@/db/db';
import { saveMany, type Change } from '@/db/repo';
import { currentSettings } from '@/auth/session';

export const farmToday = () => todayInTimeZone(FARM_TIME_ZONE);

function reminderRecord(d: DesiredReminder): Record<string, unknown> {
  return {
    id: d.id,
    animalId: d.animalId,
    type: d.type,
    dueDate: d.dueDate,
    windowEnd: d.windowEnd,
    status: 'OPEN',
    sourceEventId: d.sourceEventId,
    protocolId: d.protocolId,
    completedAt: null,
    completedBy: null,
    note: null,
  };
}

/**
 * D-5 reconciliation for one animal as reminder changes for the repo. `events` is the animal's event list
 * as it will be after the save (new, edited or deleted event applied).
 */
export async function reminderChangesFor(
  animal: AnimalRecord | DomainAnimal,
  events: readonly DomainEvent[],
  today = farmToday(),
): Promise<Change[]> {
  const protocols = (await db.healthProtocols.toArray()) as unknown as DomainProtocol[];
  const stored = (await db.reminders.where('animalId').equals(animal.id).toArray()) as unknown as Array<DomainReminder & ReminderRecord>;
  const desired = computeReminders(animal as DomainAnimal, events, protocols, currentSettings(), today);
  const changes = reconcileReminders(desired, stored);
  const byId = new Map(stored.map((r) => [r.id, r]));
  const nowIso = new Date().toISOString();
  return [
    ...changes.create.map((d): Change => ({ entity: 'reminder', op: 'upsert', record: reminderRecord(d) })),
    ...changes.update.map((u): Change => ({ entity: 'reminder', op: 'upsert', record: { ...byId.get(u.id)!, dueDate: u.dueDate, windowEnd: u.windowEnd } })),
    ...changes.close.map(
      (c): Change => ({
        entity: 'reminder',
        op: 'upsert',
        record: { ...byId.get(c.id)!, status: 'DONE', completedAt: nowIso, completedBy: null, note: c.note ?? null },
      }),
    ),
    ...changes.remove.map((id): Change => ({ entity: 'reminder', op: 'delete', id })),
  ];
}

/** EX-3: create, update or delete the expense linked to an event's cost. Returns no change when nothing differs. */
async function expenseChangeFor(event: EventRecord & { deletedAt?: string | null }): Promise<Change | null> {
  if (!EVENT_COST_CATEGORY[event.type]) return null;
  const cats = await db.expenseCategories.toArray();
  const bySystemKey = new Map(cats.filter((c) => c.systemKey && !c.deletedAt).map((c) => [c.systemKey!, { id: c.id }]));
  const draft = expenseForEvent(event as unknown as DomainEvent, bySystemKey);
  const id = recordIds.expenseFromEvent(event.id);
  const existing = (await db.expenses.get(id)) as ExpenseRecord | undefined;
  if (!draft) return existing ? { entity: 'expense', op: 'delete', id } : null;
  const next = {
    ...(existing ?? {}),
    id: draft.id,
    date: draft.date,
    categoryId: draft.categoryId,
    amountSantim: draft.amountSantim,
    coversMonths: 1,
    method: existing?.method ?? draft.method,
    animalId: draft.animalId,
    sourceEventId: draft.sourceEventId,
  };
  if (
    existing &&
    existing.date === next.date &&
    existing.categoryId === next.categoryId &&
    existing.amountSantim === next.amountSantim &&
    existing.animalId === next.animalId
  ) {
    return null;
  }
  return { entity: 'expense', op: 'upsert', record: next };
}

export interface SaveEventOptions {
  /** Live calves created by the calving form (saved before the event). */
  calves?: Array<Record<string, unknown>>;
  /** Delete the event instead of saving it. */
  deleted?: boolean;
  /** Changes to save after everything else (e.g. calves to delete with their calving). */
  after?: Change[];
}

/**
 * S3-T04 / S4-T09 — used by every event form and by delete:
 * 1. build the animal's event list including the new, edited or deleted event;
 * 2. computeReminders + reconcileReminders;
 * 3. one saveMany: calves, the event, reminder changes, then the linked expense.
 */
export async function saveEventWithEffects(event: EventRecord, opts: SaveEventOptions = {}): Promise<void> {
  const animal = await db.animals.get(event.animalId);
  if (!animal) throw new Error('Animal not found');
  const others = (await db.events.where('animalId').equals(event.animalId).toArray()).filter((e) => e.id !== event.id);
  const events = (opts.deleted ? others : [...others, event]) as unknown as DomainEvent[];
  const changes: Change[] = [
    ...(opts.calves ?? []).map((c): Change => ({ entity: 'animal', op: 'upsert', record: c })),
    opts.deleted ? { entity: 'event', op: 'delete', id: event.id } : { entity: 'event', op: 'upsert', record: event as unknown as Record<string, unknown> },
    ...(await reminderChangesFor(animal, events)),
  ];
  const expense = await expenseChangeFor(opts.deleted ? { ...event, deletedAt: new Date().toISOString() } : event);
  if (expense) changes.push(expense);
  // Each calf gets its own reminders (e.g. heifer breeding) right away.
  for (const c of opts.calves ?? []) changes.push(...(await reminderChangesFor(c as unknown as AnimalRecord, [])));
  changes.push(...(opts.after ?? []));
  await saveMany(changes);
}

/** Save several events at once (HE-3 batch vaccination / deworming): one saveMany for everything. */
export async function saveEventsWithEffects(eventsToSave: EventRecord[]): Promise<void> {
  const changes: Change[] = [];
  for (const ev of eventsToSave) {
    const animal = await db.animals.get(ev.animalId);
    if (!animal) continue;
    const others = (await db.events.where('animalId').equals(ev.animalId).toArray()).filter((e) => e.id !== ev.id);
    changes.push({ entity: 'event', op: 'upsert', record: ev as unknown as Record<string, unknown> });
    changes.push(...(await reminderChangesFor(animal, [...others, ev] as unknown as DomainEvent[])));
    const exp = await expenseChangeFor(ev);
    if (exp) changes.push(exp);
  }
  await saveMany(changes);
}

/** Reconcile the reminders of the given animals (all active animals by default). Returns the number of changes. */
export async function reconcileAllReminders(animalIds?: string[]): Promise<number> {
  const animals = animalIds ? (await db.animals.bulkGet(animalIds)).filter((a): a is AnimalRecord => !!a) : await db.animals.toArray();
  const changes: Change[] = [];
  for (const a of animals) {
    const events = (await db.events.where('animalId').equals(a.id).toArray()) as unknown as DomainEvent[];
    changes.push(...(await reminderChangesFor(a, events)));
  }
  if (changes.length) await saveMany(changes);
  return changes.length;
}

/** S3-T10: once per day per device, after a sync so another device's changes are already here. */
export async function dailyReconcile(): Promise<void> {
  const today = farmToday();
  if ((await getMeta<string>('remindersReconciledOn')) === today) return;
  await reconcileAllReminders();
  await setMeta('remindersReconciledOn', today);
}
